import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { FavoriteList } from "@yutai/shared"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Breadcrumbs } from "../../components/Breadcrumbs.js"
import { SiteFooter } from "../../components/SiteFooter.js"
import { SiteHeader } from "../../components/SiteHeader.js"
import { useAuth } from "../../lib/auth.js"
import styles from "./ListsPage.module.css"
import { RowActionMenu } from "./RowActionMenu.js"
import { useLists } from "./useLists.js"

function SortableListRow({
  list,
  onBulkRegister,
  onDelete,
}: {
  list: FavoriteList
  onBulkRegister: (id: string) => void
  onDelete: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <li ref={setNodeRef} style={style} className={styles.row}>
      <button
        type="button"
        className={styles.dragHandle}
        aria-label="並び替え"
        {...attributes}
        {...listeners}
      >
        ⠿
      </button>
      <Link to={`/lists/${list.id}`} className={styles.rowLink}>
        <span className={styles.rowName}>{list.name}</span>
        {list.isHoldingMode && <span className={styles.holdingBadge}>保有銘柄</span>}
        <span className={styles.rowCount}>{list.items.length}件</span>
      </Link>
      <RowActionMenu
        onBulkRegister={() => onBulkRegister(list.id)}
        onDelete={() => onDelete(list.id)}
        deleteLabel="リストを削除"
        ariaLabel={`「${list.name}」の操作`}
      />
    </li>
  )
}

export function ListsPage() {
  const { isLoggedIn, isLoading: authLoading } = useAuth()
  const { lists, isLoading, createList, deleteList, reorderLists } = useLists()
  const [newListName, setNewListName] = useState("")
  const navigate = useNavigate()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const name = newListName.trim()
    if (!name) return
    await createList(name)
    setNewListName("")
  }

  async function handleDelete(id: string) {
    const list = lists.find((l) => l.id === id)
    if (!list) return
    if (!window.confirm(`「${list.name}」を削除しますか？`)) return
    try {
      await deleteList(id)
    } catch (e) {
      console.error(e)
      window.alert("リストの削除に失敗しました。時間をおいて再度お試しください。")
    }
  }

  function handleBulkRegister(id: string) {
    navigate(`/lists/${id}?bulk=1`)
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = lists.findIndex((l) => l.id === active.id)
    const newIndex = lists.findIndex((l) => l.id === over.id)
    const reordered = [...lists]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    reorderLists(reordered.map((l) => l.id))
  }

  return (
    <div className={styles.page}>
      <SiteHeader />
      <Breadcrumbs
        className={styles.breadcrumbs}
        items={[{ label: "トップ", href: "/" }, { label: "マイリスト" }]}
      />

      <main className={styles.main}>
        {!authLoading && !isLoggedIn && (
          <p className={styles.notice}>マイリストを使うにはログインしてください。</p>
        )}

        {isLoggedIn && (
          <>
            <form className={styles.createForm} onSubmit={handleCreate}>
              <input
                type="text"
                className={styles.createInput}
                placeholder="新しいリスト名"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
              />
              <button type="submit" className={styles.createButton}>
                作成
              </button>
            </form>

            {isLoading && <p className={styles.notice}>読み込み中...</p>}
            {!isLoading && lists.length === 0 && (
              <p className={styles.notice}>
                まだリストがありません。一覧ページの☆ボタンから追加できます。
              </p>
            )}

            {lists.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={lists.map((l) => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className={styles.list}>
                    {lists.map((list) => (
                      <SortableListRow
                        key={list.id}
                        list={list}
                        onBulkRegister={handleBulkRegister}
                        onDelete={handleDelete}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  )
}
